import { useSearchParams } from "react-router-dom";
import React, { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { HubConnection, HubConnectionBuilder } from "@microsoft/signalr";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import heic2any from "heic2any";
import { arrow, useFloating, useInteractions, useHover, FloatingArrow } from "@floating-ui/react";
import { DateTime } from "luxon";

import { env } from "../../env";
import { requestsRepository } from "../../api/RequestsRepository";
import { ChatUser } from "../../api/models/ChatUser";
import { ChatMessage } from "../../api/models/ChatMessage";
import { EditIcon } from "../../components/icons/EditIcon";
import { ReplyIcon } from "../../components/icons/ReplyIcon";
import { SendIcon } from "../../components/icons/SendIcon";
import { Messege } from "../../components/Messege/Messege";
import { formatDate, timeDiff } from "../../utils/formatDate";
import { CloseIcon } from "../../components/icons/CloseIcon";
import { AttachmentIcon } from "../../components/icons/AttachmentIcon";
import { FileIcon } from "../../components/icons/FileIcon";
import { DeleteIcon } from "../../components/icons/DeleteIcon";
import { useDetectOutsideClick } from "../../hooks/useDetectOutsideClick";
import { SettingsIcon } from "../../components/icons/SettingsIcon";
import { SearchIcon } from "../../components/icons/SearchIcon";
import { RecordHistory } from "../../api/models/RecordHistory";
import {readUserMessage, setNewMessagesCount} from "../../redux/actions/Chat";
import { ReduxState } from "../../redux/store";
import { Chat } from "../../api/models/Chat";
import { getStattus } from "../../utils/common";
import { RecordStatus } from "../../api/models/RecordStatus";
import { TmLoader } from "../Loader/TmLoader";
import { PlusIcon } from "../icons/PlusIcon";
import { AddContact } from "../Modals/AddContact/AddContact";
import { addModal, removeModal } from "../../redux/actions/Modals";
import { ActionFn, UiModal } from "../Modals/UiModals";
import { PRIVATE_CHAT, ROBOT_ID } from "../../api/models/consts";
import { CallIcon } from "../icons/CallIcon";
import { CameraBlackIcon } from "../icons/CameraBlackIcon";
import { CallBlackIcon } from "../icons/CallBlackIcon";
import { addCall } from "../../redux/actions/Online";
import { UserPhoto } from "../UserPhoto/UserPhoto";
import { DeleteChat } from "../Modals/DeleteChat/DeleteChat";
import { NavigationTabs } from "../NavigationTabs/NavigationTabs";
import { DeleteMessage } from "../Modals/DeleteMessage/DeleteMessage";
import { BackIcon } from "../icons/BackIcon";
import { UserInformation } from "../Modals/UserInformation/UserInformation";
import { SortIcon } from "../icons/SortIcon";
import { SortDirection } from "../../api/models/SortDirection";
import { SelectedIcon } from "../icons/SelectedIcon";
import { DownloadIcon } from "../icons/DownloadIcon";
import { EmptyIcon } from "../icons/EmptyIcon";
import { ArrowUpIcon } from "../icons/ArrowUpIcon";
import { ArrowDownIcon } from "../icons/ArrowDownIcon";

import "./Messenger.scss";

interface IProps {
    fullMode: boolean;
    userId?: string | null;
    externalRoomId?: string | null;
    link?: string | null;
    visible: boolean;
    showChatsList?: boolean;
    authSessionId: any;
    serviceType: any;
    contactsMode?: boolean;
    fullChatMode?: boolean;
    roomId?: string | null;
    contextUrl?: string | null;
    changePage?: (value: any) => void
}

export const Messenger = (props: IProps) => {
    const { fullMode, userId, visible, externalRoomId, link, showChatsList, authSessionId, serviceType, fullChatMode, 
        roomId, contactsMode, changePage } = props;

    const [params] = useSearchParams();
    
    const dispatch = useDispatch();
    
    const { newMessagesCount } = useSelector(({chat}: ReduxState) => chat);
    const { onlineUsers } = useSelector(({online}: ReduxState) => online);
    
    const [connection, setConnection] = useState<null | HubConnection>(null);
    const [room, setRoom] = useState<string | null>();
    const [message, setMessage] = useState<string>("");
    const [roomUserId, setRoomUserId] = useState<string | null>(userId ?? null);
    const [portalChatUser, setPortalChatUser] = useState<string | null>(null);
    const [userOrganization, setUserOrganization] = useState<string | null>( null);
    const [userName, setUserName] = useState<string | null>( null);
    const [chatId, setChatId] = useState<string | null>(null);
    const [chatName, setChatName] = useState<string | null>(null);
    const [roomContext, setRoomContext] = useState<string | null>(null);
    const [replyMessageId, setReplyMessageId] = useState<string | null>(null);
    const [editMessageId, setEditMessageId] = useState<string | null>(null);
    const [users, setUsers] = useState<ChatUser[]>([]);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [unreadMessages, setUnreadMessages] = useState<ChatMessage[]>([]);
    const [chats, setChats] = useState<any>([]);
    const [searchChats, setSearchChats] = useState<any>([]);
    const [searchChatInput, setSearchChatInput] = useState<string>("");
    const [searchMessageInput, setSearchMessageInput] = useState<string>("");
    const [newMessages, setNewMessages] = useState<any>([]);
    const [deleteMessages, setDeleteMessages] = useState<any>([]);
    const [editMessages, setEditMessages] = useState<any>([]);
    const [showSettings, setShowSettings] = useState<any>(null);
    const [showSort, setShowSort] = useState<any>(null);
    const [showRecords, setShowRecords] = useState<boolean>(false);
    const [showSearchMessegies, setShowSearchMessegies] = useState<boolean>(false);
    const [records, setRecords] = useState<RecordHistory[]>([]);
    const [showChatSettings, setShowChatSettings] = useState<boolean>(false);
    const [showCallSettings, setShowCallSettings] = useState<boolean>(false);
    const [scrollToEnd, setScrollToEnd] = useState(false);
    const [scrollToMessage, setScrollToMessage] = useState<string | null>(null);
    const [findMessageId, setFindMessageId] = useState<string | null>(null);
    const [sendFile, setSendFile] = useState(false);
    const [attachments, setAttachments] = useState<any>([]);
    const [newAttachments, setNewAttachments] = useState<any>([]);
    const [deleteAttachments, setDeleteAttachments] = useState<any>([]);
    const [readMessages, setReadMessages] = useState<any>([]);
    const [newUser, setNewUser] = useState<ChatUser[]>([]);
    const [newChat, setNewChat] = useState<Chat[]>([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [showList, setShowList] = useState(showChatsList);
    const [connected, setConnected] = useState(false);
    const [maxFileSize, setMaxFileSize] = useState(1);
    const [canWrite, setCanWrite] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [removeUnsendMessages, setRemoveUnsendMessages] = useState(false);
    const [newEmptyUser, setNewEmptyUser] = useState<any>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.New);

    const arrowRef = useRef<any>(null);
    const {refs, floatingStyles, context} = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement: "top-end",
        middleware: [arrow({
            element: arrowRef,
        })
        ]
    });
    const hover = useHover(context);

    const {getReferenceProps, getFloatingProps} = useInteractions([
        hover,
    ]);
    
    const messagesEndRef = useRef<any>(null);
    const ref = useRef<HTMLDivElement | null>(null);
    const sortRef = useRef<HTMLDivElement | null>(null);
    const callRef = useRef<HTMLDivElement | null>(null);
    const textareaRef = useRef<any>(null);
    
    useDetectOutsideClick(ref, () => setShowChatSettings(false));
    useDetectOutsideClick(callRef, () => setShowCallSettings(false));
    useDetectOutsideClick(sortRef, () => setShowSort(false));

    useEffect(() => {
        const connect = new HubConnectionBuilder()
            .withUrl(env().REACT_APP_CHAT_HUB_URL ?? "")
            .withAutomaticReconnect()
            .build();

        setConnection(connect);
    }, []);

    useEffect(() => {
        if (userId) {
            setRoomUserId(userId);
        }
    }, [userId]);
    
    useEffect(() => {
        if (newEmptyUser) {
            setRoomUserId(contactsMode ? newEmptyUser.chatPortalId : newEmptyUser.id);
            setNewEmptyUser(null);
        }
    }, [newEmptyUser, contactsMode]);

    useLayoutEffect(() => {
        return () => {
            if (connection) {
                connection?.stop().then(() => {
                    setConnection(null);
                });
            }
        }
    }, [connection]);

    useEffect(() => {
        if (authSessionId) {
            requestsRepository.ChatApiService.getUserInfo(authSessionId).then((res) => {
                if (res.success) {
                    setPortalChatUser(res.result.roleContext?.chatportal);
                    setUserName(res.result.roleContext?.fullName);
                    setUserOrganization(res.result.user?.organizationName ?? null)
                }
            })
        }
    }, [authSessionId]);

    useEffect(() => {
        if (connection !== null) {
            connection.start().then(() => {
                setConnected(true);
                if (!fullMode) {
                    setRoom(params.get('roomId'));
                    const request: any = {
                        userId: roomUserId,
                        roomId: params.get('roomId') ?? roomId,
                        sessionId: authSessionId,
                        serviceType
                    }
                    sendMessageSignal("JoinChat", request);
                } else {
                    const request: any = {
                        userId: userId,
                        sessionId: authSessionId
                    }
                    sendMessageSignal("JoinEmptyChat", request);
                }
            });
        }
    }, [connection]);

    useEffect(() => {
        if (fullMode && connected) {
            setSortDirection(SortDirection.New);
            if (chatId) {
                sendMessageSignal("LeaveChat" , null);
                setTimeout(() => {
                    const request: any = {
                        userId: userId,
                        sessionId: authSessionId
                    }
                    sendMessageSignal("JoinEmptyChat", request);
                }, 1000)
            }
            getChats();
        }
    }, [contactsMode, connected]);

    const getChats = () => {
        setChats([]);
        setSearchChats([]);
        setUsers([]);
        setChatName(null);
        setRoomContext(null);
        requestsRepository.ChatApiService.getChats(props.userId ?? null, authSessionId, props.contactsMode ? "1" : null).then((res) => {
            if (res.success) {
                setChats(res.result);
                setSearchChats(res.result);
                if (res.result.length > 0 && !fullChatMode) {
                    const room = res.result.find(x => x.roomId === externalRoomId);
                    const chatRoomId = room?.roomId ?? res.result[0].roomId;
                    setRoom(chatRoomId);
                    const request: any = {
                        userId: props.userId,
                        roomId: chatRoomId,
                        sessionId: authSessionId,
                        serviceType
                    }
                    sendMessageSignal("JoinChat", request);
                } else {
                    setChatId(null);
                    setRoom(null);
                    setChatName(null);
                    setRoomContext(null);
                    setMessages([]);
                }
            }
        });
    }

    useEffect(() => {
        if (connection !== null) {
            connection?.on("JoinChatResponse", (message: any) => {
                if (message.message?.length > 0) {
                    console.error(message.message);
                } else {
                    setChatId(message.chatId);
                    setRoomUserId(message.id);
                    setChatName(message.chatName);
                    setRoomContext(message.roomContext);
                    setIsAdmin(message.isAdmin);
                    requestsRepository.ChatApiService.getChatUserAccess(message.chatId, message.id).then((res) => {
                        if (res.success && res.result.canWrite) {
                            setCanWrite(true);
                        }
                    })
                    requestsRepository.ChatApiService.getUsers(message.chatId).then(res => {
                        if (res.success) {
                            setUsers(res.result);
                        }
                    });
                    requestsRepository.ChatApiService.getChatUnreadMessages(message.chatId, message.id).then((res) => {
                        if (res.success) {
                            setUnreadMessages(res.result);
                            dispatch(setNewMessagesCount(res.result.length));
                        }
                        requestsRepository.ChatApiService.getMessages(message.chatId, {headers: {"service-type":serviceType}}).then(res => {
                            if (res.success) {
                                setMessages(res.result);
                                setScrollToEnd(false);
                            }
                        });
                    });
                }
            });
            connection?.on("JoinEmptyChatResponse", (message: any) => {
                if (message.message?.length > 0) {
                    toast.error(message.message);
                } else {
                    setNewEmptyUser(message);
                }
            });
            connection?.on("NewUser", (message: any) => {
                setNewUser([...newUser, message]);
            });
            connection?.on("NewMessage", (message: any) => {
                if (message.message?.length > 0) {
                    toast.error(message.message);
                } else {
                    setNewMessages([...newMessages, message]);
                }
            });
            connection?.on("SendMessageResponse", (message: any) => {
                if (message.message?.length > 0) {
                    toast.error(message.message);
                    setRemoveUnsendMessages(true);
                }
            });
            connection?.on("EditMessage", (message: any) => {
                if (message.message?.length > 0) {
                    toast.error(message.message);
                } else {
                    setEditMessages([...editMessages, message]);
                }
            });
            connection?.on("DeleteMessage", (message: any) => {
                if (message.message?.length > 0) {
                    toast.error(message.message);
                } else {
                    setDeleteMessages([...deleteMessages, message]);
                }
            });
            connection?.on("ReadMessage", (message: any) => {
                if (message.message?.length > 0) {
                    toast.error(message.message);
                } else {
                    setReadMessages([...readMessages, message]);
                }
            });
            connection?.on("NewChat", (message: any) => {
                setNewChat([...newChat, message.chat]);
            });
            connection?.on("ChatMaxFileSizeResponse", (message: any) => {
                setMaxFileSize(message?.maxFileSize)
            });
        }
    }, [connection]);

    useEffect(() => {
        if (removeUnsendMessages) {
            setMessages([...messages.filter(x => x.id !== "-1")]);
            setRemoveUnsendMessages(false);
        }
    }, [removeUnsendMessages]);

    useEffect(() => {
        if (newUser.length > 0) {
            newUser.forEach(item => {
                if (!users.find(x => x.id === item.id)) {
                    setUsers([...users, item]);
                }
            });
            setNewUser([]);
        }
    }, [newUser]);
    
    useEffect(() => {
        if (newChat.length > 0 && fullMode) {
            newChat.forEach(item => {
                const chatsData = [item, ...chats];
                setChats(chatsData);
                const sortedChats = sortChats(chatsData, sortDirection);
                setSearchChats(searchChatInput?.trim()?.length > 0 ? sortedChats.filter((x: any) => x.room.name?.toLowerCase().includes(searchChatInput.toLowerCase())) : sortedChats);
            });
            setNewChat([]);
        }
    }, [newChat]);
    

    useEffect(() => {
        if (newMessages.length > 0) {
            newMessages.forEach((x: any) => {
                if (x.chatId === chatId) {
                    if (x.userId === roomUserId) {
                        const data = [...messages];
                        const idx = data.findIndex(d => d.id === "-1");
                        if (idx !== -1) {
                            data[idx].id = x.id;
                            data[idx].createdDate = x.createdDate;
                            setMessages(data);
                        }
                        if (fullMode) {
                            let chatsData = [...chats];
                            const chatIdx = chatsData.findIndex(c => c.id === x.chatId);
                            chatsData[chatIdx].messages = [data[idx]];
                            const chatWithMessage = chatsData[chatIdx];
                            chatsData = chatsData.filter(c => c.id !== x.chatId);
                            chatsData.unshift(chatWithMessage);
                            setChats(chatsData);
                            const sortedChatsData = sortChats(chatsData, sortDirection);
                            setSearchChats(searchChatInput?.trim()?.length > 0 ? sortedChatsData.filter((x: any) => x.room.name?.toLowerCase().includes(searchChatInput.toLowerCase())) : sortedChatsData);
                        }
                    } else {
                        dispatch(setNewMessagesCount(newMessagesCount + 1));
                        const messageData = {
                            id: x.id,
                            userId: x.userId,
                            messageText: x.messageText,
                            createdDate: x.createdDate,
                            replyMessageId: x.replyMessageId,
                            attachments: x.attachments,
                            isRead: x.isRead
                        };
                        setMessages([...messages, messageData]);
                        if (fullMode) {
                            let chatsData = [...chats];
                            const chatIdx = chatsData.findIndex(c => c.id === x.chatId);
                            chatsData[chatIdx].unreadUserMessagesCount = chatsData[chatIdx].unreadUserMessagesCount + 1;
                            chatsData[chatIdx].messages = [messageData];
                            const chatWithMessage = chatsData[chatIdx];
                            chatsData = chatsData.filter(c => c.id !== x.chatId);
                            chatsData.unshift(chatWithMessage);
                            setChats(chatsData);
                            const sortedChatsData = sortChats(chatsData, sortDirection);
                            setSearchChats(searchChatInput?.trim()?.length > 0 ? sortedChatsData.filter((x: any) => x.room.name?.toLowerCase().includes(searchChatInput.toLowerCase())) : sortedChatsData);
                        }
                        setUnreadMessages([...unreadMessages, messageData]);
                    }
                } else if (fullMode) {
                    let chatsData = [...chats];
                    const chatIdx = chatsData.findIndex(c => c.id === x.chatId);
                    if (chatIdx !== -1) {
                        const messageData = {
                            id: x.id,
                            userId: x.userId,
                            messageText: x.messageText,
                            createdDate: x.createdDate,
                            replyMessageId: x.replyMessageId,
                            attachments: x.attachments,
                            isRead: x.isRead
                        };
                        chatsData[chatIdx].unreadUserMessagesCount = chatsData[chatIdx].unreadUserMessagesCount + 1;
                        chatsData[chatIdx].messages = [messageData];
                        const chatWithMessage = chatsData[chatIdx];
                        chatsData = chatsData.filter(c => c.id !== x.chatId);
                        chatsData.unshift(chatWithMessage);
                        setChats(chatsData);
                        const sortedChatsData = sortChats(chatsData, sortDirection);
                        setSearchChats(searchChatInput?.trim()?.length > 0 ? sortedChatsData.filter((x: any) => x.room.name?.toLowerCase().includes(searchChatInput.toLowerCase())) : sortedChatsData);
                        setUnreadMessages([...unreadMessages, messageData]);
                    } else {
                        requestsRepository.ChatApiService.getChat(x.chatId, userId ?? null, authSessionId).then((chatResult) => {
                            if (chatResult.success && chatResult.result.users.find(user => user.userId !== ROBOT_ID && (contactsMode ? user.systemId === "chatportal" : user.systemId !== "chatportal"))) {
                                const chatsData = sortChats([chatResult.result, ...chats], sortDirection);
                                setChats([chatResult.result, ...chats]);
                                setSearchChats(searchChatInput?.trim()?.length > 0 ? chatsData.filter((x: any) => x.room.name?.toLowerCase().includes(searchChatInput.toLowerCase())) : chatsData);
                            }
                        })
                    }
                }
            });
            setNewMessages([]);
            setScrollToEnd(false);
        }
    }, [newMessages]);

    useEffect(() => {
        if (editMessages.length > 0) {
            editMessages.forEach((x: any) => {
                if (x.chatId === chatId) {
                    const data = [...messages];
                    const idx = data.findIndex(d => d.id === x.id);
                    data[idx].messageText = x.messageText;
                    data[idx].attachments = [...data[idx].attachments?.filter(a => !x.deleteAttachments.includes(a.id)), ...x.attachments];
                    setMessages(data);
                }
            });
            setEditMessages([]);
        }
    }, [editMessages]);

    useEffect(() => {
        if (deleteMessages.length > 0) {
            deleteMessages.forEach((x: any) => {
                if (x.chatId === chatId) {
                    setMessages([...messages.filter(d => d.id !== x.id)]);
                }
            });
            setDeleteMessages([]);
        }
    }, [deleteMessages]);

    useEffect(() => {
        if (readMessages.length > 0) {
            readMessages.forEach((x: any) => {
                if (x.chatId === chatId) {
                    const data = [...messages];
                    const idx = data.findIndex(d => d.id === x.id);
                    if (idx !== -1) {
                        data[idx].isRead = true;
                        setMessages(data);
                    }
                }
            });
            setReadMessages([]);
        }
    }, [readMessages]);

    useEffect(() => {
        if (visible && !scrollToEnd && users.length > 0 && messages.length > 0) {
            const unreadElement: any = document.querySelector('.unread');
            if (messagesEndRef.current) {
                let scroll =
                    messagesEndRef.current.scrollHeight -
                    messagesEndRef.current.clientHeight;
                if (unreadElement) {
                    scroll = unreadElement.offsetTop;
                }

                messagesEndRef.current.scrollTo(0, scroll);
            }
            setScrollToEnd(true);
        }
    }, [messages, users, scrollToEnd, visible]);

    useEffect(() => {
        if (scrollToMessage !== null) {
            const element: any = document.getElementById(scrollToMessage);
            if (element) {
                const scroll = element.offsetTop - element.clientHeight - 200;
                messagesEndRef.current.scrollTo(0, scroll);
            }

            setScrollToMessage(null);
        }
    }, [scrollToMessage]);

    useEffect(() => {
        searchMessageInChat();
    }, [searchMessageInput]);

    useLayoutEffect(() => {
        if (textareaRef?.current) {
            textareaRef.current.style.height = "inherit";
            textareaRef.current.style.height = `${message.length > 0 ? Math.max(
                textareaRef.current.scrollHeight,
                26
            ) : 26}px`;
        }
    }, [message]);

    const sendMessageSignal = (methodName: any, request: any) => {
        if (connection !== null) {
            if (request !== null) {
                connection.invoke(methodName, request);
            } else {
                connection.invoke(methodName);

            }
        }
    }

    const getUser = (userId: any) => {
        return users.find(x => x.userId === userId);
    }

    const getMessage = (id: any) => {
        return messages.find(x => x.id === id);
    }

    const sendMessage = () => {
        if (roomUserId && chatId && !isSendDisabled()) {
            if (editMessageId !== null) {
                const attachmentsData = attachments && attachments?.length > 0 ? attachments?.filter((x: any) => newAttachments.find((newAttach: any) => x.id === newAttach.id)) : [];
                const request = {userId: roomUserId, chatId, message, messageId: editMessageId, attachments: attachmentsData, deleteAttachments};
                sendMessageSignal("EditMessage", request);
                const data = [...messages];
                const idx = data.findIndex(d => d.id === editMessageId);
                data[idx].messageText = message;
                data[idx].attachments = attachments;
                setMessages(data);
                setMessage("");
                setAttachments([]);
                setNewAttachments([]);
                setDeleteAttachments([]);
                setEditMessageId(null);
            } else {
                const data = [...messages, {
                    id: "-1",
                    userId: roomUserId,
                    messageText: message,
                    createdDate: new Date(),
                    attachments,
                    replyMessageId: replyMessageId,
                    isRead: false
                }];
                const request = {userId: roomUserId, chatId, message, replyMessageId, attachments};
                setMessages(data);
                setAttachments([]);
                setMessage("");
                setReplyMessageId(null);
                sendMessageSignal("SendMessage", request);
                setScrollToEnd(false);
            }
        }
    }

    const deleteMessage = (messageId: any) => {
        if (roomUserId && chatId && messageId !== null) {
            const component = DeleteMessage({
                onSuccess: () => {
                    const request = {userId: roomUserId, chatId, messageId};
                    sendMessageSignal("DeleteMessage", request);
                    setMessages([...messages.filter(x => x.id !== messageId)]);
                    close();
                },
                 isMobile: externalRoomId ? externalRoomId.length > 0 : false
            });
            const close: ActionFn = () => {
                dispatch(removeModal(modal))
            };
            const modal: UiModal = {component , close};
            dispatch(addModal(modal));
        }
    }

    const readMessage = (id: any, updateMessage: any) => {
        if (roomUserId && chatId) {
            const request = {userId: roomUserId, chatId, messageId: id, updateMessage};
            sendMessageSignal("ReadMessage", request);
            setUnreadMessages([...unreadMessages.filter(x => x.id !== id)]);
            if (newMessagesCount > 0) {
                dispatch(readUserMessage());
            }
            if (fullMode) {
                const chatsData = sortChats([...chats], sortDirection);
                const chatIdx = chatsData.findIndex(c => c.id === chatId);
                chatsData[chatIdx].unreadUserMessagesCount = chatsData[chatIdx].unreadUserMessagesCount - 1;
                setChats(chatsData);
                setSearchChats(searchChatInput?.trim()?.length > 0 ? chatsData.filter((x: any) => x.room.name?.toLowerCase().includes(searchChatInput.toLowerCase())) : chatsData);
            }
        }
    }

    const groupBy = (xs: any, key: any) => {
        return xs.reduce((rv: any, x: any) => {
            (rv[x[key]] = rv[x[key]] || []).push(x);
            return rv;
        }, {});
    };

    const getGroupedMessages = (key: any = null) => {
        var data = groupBy(messages.map(x => ({...x, groupDate: formatDate(x.createdDate, 'MM-DD-yyyy')})), 'groupDate');
        return key !== null ? data[key] : data;
    }

    const getDateText = (date: any) => {
        return (new Date()).toDateString() === (new Date(date)).toDateString() ? 'Сегодня' : formatDate(new Date(date), 'DD MMMM');
    }

    const onChangeFile = useCallback(
        async (data: any) => {
            if (data && data.currentTarget && data.currentTarget.files && data.currentTarget.files.length > 0) {
                if (env().REACT_APP_CHAT_FILE_TYPES?.length > 0) {
                    const fileTypes = env().REACT_APP_CHAT_FILE_TYPES.split(',');
                    for (const file of data.currentTarget.files) {
                        if (!fileTypes.includes(file.type)) {
                            toast.error('Недопустимый формат файла')
                            return;
                        }
                    }
                }
                for (const file of data.currentTarget.files) {
                    const filesize = +((file?.size / 1024) / 1024).toFixed(4);
                    if (filesize >= maxFileSize) {
                        toast.error(`Превышен лимит по объему файла. Допустимый объем файла – ${maxFileSize} Мб.`);
                        return;
                    }
                }
                setSendFile(true);
                try {
                    const attachmentsData = [];
                    for (const file of data.currentTarget.files) {
                        const name = file.name;
                        let type = file.type;
                        let reqFile = file;
                        if (name?.toLowerCase()?.includes('heic')|| name?.toLowerCase()?.includes('heif')) {
                            const heicRes = await heic2any({ blob: file });
                            reqFile = new File(Array.isArray(heicRes) ? heicRes : [heicRes], file.name?.replace('.heic', '.png')?.replace('.heif', '.png')?.replace('.HEIC', '.png')?.replace('.HEIF', '.png'), {type: "image/png"});
                            type = "image/png";
                        }
                        const result = await requestsRepository.AttachmentApiService.addAttachment(reqFile,
                            {headers: {contentType: 'text/plain; charset=utf-8', "service-type":serviceType}}
                        );
                        if (result?.success) {
                            const attachment = {
                                id: result.result.id,
                                url: result.result.url,
                                fileName: name,
                                isImage: type?.includes('image')
                            };
                            attachmentsData.push(attachment);
                        }
                    }
                    setAttachments(attachments?.length > 0 ? [...attachments, ...attachmentsData] : [...attachmentsData]);
                    if (editMessageId) {
                        setNewAttachments([...newAttachments, ...attachmentsData]);
                    }
                } finally {
                    setSendFile(false);  
                }
            }
        },
        [attachments, sendFile, editMessageId, newAttachments, maxFileSize]
    );

    const removeAttachment = (attachment: any) => {
        if (editMessageId && !newAttachments.includes(attachment)) {
            setDeleteAttachments([...deleteAttachments, attachment.id]);
        }
        setAttachments(attachments?.filter((x: any) => x.id !== attachment.id) ?? []);
    }

    const closeEdit = () => {
        setEditMessageId(null);
        setAttachments([]);
        setDeleteAttachments([]);
        setNewAttachments([]);
        setMessage("");
    }

    const isSendDisabled = () => {
        return !canWrite || (message?.trim()?.length === 0 && attachments?.length === 0);
    }

    const onShowRecords = () => {
        setShowChatSettings(false);
        setShowSearchMessegies(false);
        setShowRecords(true);
        if (room && roomUserId) {
            requestsRepository.RoomApiService.getRecords(room).then(res => {
                if (res.success) {
                    setRecords(res.result);
                }
            })
        }
    }

    const onShowUsers = () => {
        setShowChatSettings(false);
        const component = UserInformation({
            users,
            isAdmin,
            userId: roomUserId,
            chatId: chatId,
            authSessionId,
            changeChatAccess,
            changeIsAdminAccess,
            isMobile: externalRoomId ? externalRoomId.length > 0 : false
        });
        const close: ActionFn = () => {dispatch(removeModal(modal))};
        const modal: UiModal = {component, close};
        dispatch(addModal(modal));
    }
    
    const onDeleteChat = () => {
        setShowChatSettings(false);
        setShowRecords(false);
        setShowSearchMessegies(false);
        const user = users.find(x => x.userId === portalChatUser);
        if (user && user.roomUserTitle && chatId) {
            const component = DeleteChat({
                userName: user.roomUserTitle,
                userId: user.userId,
                chatId,
                onSuccess: () => {                    
                    sendMessageSignal("LeaveChat" , null);
                    getChats();
                    close();
                    setTimeout(() => {
                        const request: any = {
                            userId: userId,
                            sessionId: authSessionId
                        }
                        sendMessageSignal("JoinEmptyChat", request);
                    }, 1000)
                }
            });
            const close: ActionFn = () => {
                dispatch(removeModal(modal))
            };
            const modal: UiModal = {component , close};
            dispatch(addModal(modal));
        }
    }

    const onShowSearchMessegies = () => {
        setShowChatSettings(false);
        setShowRecords(false);
        setShowSearchMessegies(true);
    }

    const backToChat = () => {
        setShowRecords(false);
        setShowSearchMessegies(false);
    }

    const onSearchChat = () => {
        const chatsList = sortChats(chats, sortDirection);
        setSearchChats(searchChatInput?.trim()?.length > 0 ? chatsList.filter((x: any) => x.room.name?.toLowerCase().includes(searchChatInput.toLowerCase())) : chatsList);
    }

    const changeChat = (chat: any) => {
        sendMessageSignal("LeaveChat", null);
        setMessages([]);
        setMessage("");
        setAttachments([]);
        setNewAttachments([]);
        setDeleteAttachments([]);
        setEditMessageId(null);
        setChatName(null);
        setRoom(chat.roomId);
        setReplyMessageId(null);
        const request: any = {
            userId: props.contactsMode ? null : roomUserId,
            roomId: chat.roomId,
            sessionId: authSessionId,
            serviceType
        }
        sendMessageSignal("JoinChat", request);
        setShowChatSettings(false);
        setShowRecords(false);
        setShowSearchMessegies(false);
        setShowList(false);
        setCanWrite(false);
    }

    const changeChatAccess = (userId: string) => {
        const data = [...users];
        const idx = data.findIndex(d => d.userId === userId);
        data[idx].canRead = false;
        setUsers(data);
    }
    
    const changeIsAdminAccess = (userId: string, isAdmin: boolean) => {
        const data = [...users];
        const idx = data.findIndex(d => d.userId === userId);
        data[idx].isAdmin = isAdmin;
        setUsers(data);
    }

    const showAddContact = () => {
        const component = AddContact({
            userId: portalChatUser ?? "",
            userName: userName ?? "",
            description: userOrganization ?? "",
            sessionId: authSessionId,
            chats: chats,
            onSuccess: () => {
                sendMessageSignal("LeaveChat" , null);
                close();
                getChats();
                setTimeout(() => {
                    const request: any = {
                        userId: userId,
                        sessionId: authSessionId
                    }
                    sendMessageSignal("JoinEmptyChat", request);
                }, 1000)
            }
        });
        const close: ActionFn = () => {dispatch(removeModal(modal))};
        const modal: UiModal = {component, close};
        dispatch(addModal(modal));
    }
    
    const getPrivatChatTitle = (users: ChatUser[]) => {
        const user = users.find(x => x.userId === portalChatUser);
        return user?.roomUserTitle ?? "";
    }
    
    const isOnline = (users: ChatUser[]) => {
        const user = users.find(x => x.userId !== ROBOT_ID && x.userId !== portalChatUser);
        return onlineUsers.find(x => x === user?.userId);
    }
    
    const getPrivatChatDescription = (users: ChatUser[]) => {
        const user = users.find(x => x.userId === portalChatUser);
        return user?.description ?? "";
    }

    const onCall = (isAudio: boolean) => {
        setShowCallSettings(false);
        const user = users.find(x => x.userId !== ROBOT_ID && x.userId !== portalChatUser);
        const callerUser = users.find(x => x.userId === portalChatUser);
        if (user && callerUser && room) {
            dispatch(addCall({isAudio, userId: user.userId, userName: user.userName, callerId: callerUser.userId, callerName: callerUser.userName, roomId: room}));
        }
    }
    
    const sendDisabled = useMemo(() => {
        return !canWrite || !chatName || chatName.length === 0;
    }, [canWrite, chatName]);
    
    const getPhotoUserId = (users: any[]) => {
        return users.find(x => x.userId !== portalChatUser && x.userId !== ROBOT_ID)?.userId;
    }

    const toProcess = () => {
        if (!props.contactsMode && roomContext && props.contextUrl && props.contextUrl.length > 0) {
            const blankUrl = `${props.contextUrl}${roomContext}`;
            window.location.href = blankUrl;
        }
    }
    
    const sortMessages = (direction: SortDirection) => {
        setSortDirection(direction);
        setShowSort(false);
        setSearchChats(sortChats(searchChats, direction));
    }
    
    const sortChats = (searchChats: any[], direction: SortDirection) => {
        let sortindData = [...searchChats];
        if (direction === SortDirection.New) {
            sortindData = sortindData.sort((a: any, b: any) => ((a.mesages?.[0]?.createdDate ? a.messages[0].createdDate : a.updatedDate) > (b.mesages?.[0]?.createdDate ? b.messages[0].createdDate : b.updatedDate) ? -1 : 1));
        } else if (direction === SortDirection.Old) {
            sortindData = sortindData.sort((a: any, b: any) => ((a.mesages?.[0]?.createdDate ? a.messages[0].createdDate : a.updatedDate) < (b.mesages?.[0]?.createdDate ? b.messages[0].createdDate : b.updatedDate) ? -1 : 1));
        } else {
            sortindData = sortindData.sort((a: any, b: any) => ((a.mesages?.[0]?.createdDate ? a.messages[0].createdDate : a.updatedDate) > (b.mesages?.[0]?.createdDate ? b.messages[0].createdDate : b.updatedDate) ? -1 : 1))
                .sort((a: any, _) => a.unreadUserMessagesCount > 0 ? -1 : 0);
        }
        return sortindData;
    }
    
    const openVideoLink = () => {
        setShowSettings(false);
        const url = new URL(link ?? window.location.origin+'/'+env().REACT_APP_ROOT_FOLDER+'/');
        url.searchParams.append('roomId', room ?? "");
        if (env().REACT_APP_USE_TM_AUTH) {
            url.searchParams.append('sessionId', authSessionId ?? "");
        } else {
            url.searchParams.append('userId', roomUserId ?? "");
        }
        const blankUrl = `${url.origin}${url.pathname}${url.search}`;
        window.open(blankUrl, '_blank');
    }
    
    const closeMessageSearch = () => {
        setShowSearchMessegies(false);
        setSearchMessageInput("");
    }
    
    const filteredMessages  = useMemo(() => {
        if (searchMessageInput.trim().length > 0) {
            return messages.filter(x => x.messageText?.toLowerCase()?.includes(searchMessageInput.toLowerCase()))
        }
        return [];
    }, [messages, searchMessageInput]);
    
    const searchNextMessage = () => {
        const currentIdx = filteredMessages.findIndex(x => x.id === findMessageId);
        if (currentIdx + 1 < filteredMessages.length) {
            const message = filteredMessages[currentIdx + 1];
            setFindMessageId(message.id);
            setScrollToMessage(message.id);
        }
    }
    
    const searchPrevMessage = () => {
        const currentIdx = filteredMessages.findIndex(x => x.id === findMessageId);
        if (currentIdx - 1 >= 0) {
            const message = filteredMessages[currentIdx - 1];
            setFindMessageId(message.id);
            setScrollToMessage(message.id);
        }
    }
    
    const searchMessageInChat = () => {
        if (searchMessageInput.trim().length > 0) {
            const filteredMessages = messages.filter(x => x.messageText?.toLowerCase()?.includes(searchMessageInput.toLowerCase()));
            if (filteredMessages?.length > 0) {
                const message = filteredMessages[filteredMessages.length - 1];
                setFindMessageId(message.id);
                setScrollToMessage(message.id);
            }
        }
    }
    
    const getMessageTime = (date: string) => {
        const diff = DateTime.fromJSDate(new Date()).diff(DateTime.fromJSDate(new Date(date)), ['days', 'hours']);
        if (diff.days > 7) {
            return formatDate(new Date(date), "dd.MMM.yy");
        }
        if (diff.days === 0) {
            return formatDate(new Date(date), "HH:mm");
        }
        if (diff.days === 1) {
            return 'Вчера';
        }
        return formatDate(new Date(date), "DDDD");
    }
    
    const replyMessage =  <div
            className={`message-wrapper`}>
            <div className="message-box-wrapper message-box-wrapper-reply message-box-wrapper-edit">
                <ReplyIcon/>
                <div className={`message-box ${externalRoomId ? 'message-box_mobile' : ''}`}>
                    <div
                        className="message-box-author">{getUser(getMessage(replyMessageId)?.userId)?.userName ?? "Гость"}</div>
                    <div className="message-box-edit-text">
                        {getMessage(replyMessageId)?.messageText}
                        <div className="attachments-container">
                            {getMessage(replyMessageId)?.attachments?.map((attachment: any, key: number) => <div className="attachment-item" key={key}>
                                <div className="attachment-item__text"><FileIcon />{attachment.fileName}</div>
                            </div>)}
                        </div>
                    </div>
                </div>
                <div className="message-box-reply-close" onClick={() => setReplyMessageId(null)}><CloseIcon size={10} /></div>
            </div>
        </div>;
    
    const editMessage = <div
        className={`message-wrapper`}>
        <div className="message-box-wrapper message-box-wrapper-reply message-box-wrapper-edit">
            <EditIcon/>
            <div className={`message-box ${externalRoomId ? 'message-box_mobile' : ''}`}>
                <div className="message-box-author">Редактирование сообщения</div>
                <div className="message-box-edit-text">
                    {getMessage(editMessageId)?.messageText}
                </div>
            </div>
            <div className="message-box-reply-close" onClick={() => closeEdit()}><CloseIcon /></div>
        </div>
    </div>;
    
    const recordsList = <>
        <div className="chat-back-to-list" style={{display: 'flex', paddingTop: 0}} onClick={backToChat}>
            <BackIcon /> Назад к чату
        </div>
        {records.filter((x: any) => x.endTime !== null).length > 0 ?
        <div className="chat-records-wrapper">
        {records.filter((x: any) => x.endTime !== null).map((record: any, key: number) =>
            <div onClick={() => {
                if (record.status !== RecordStatus.Available) {
                    return;
                }
                setShowSettings(false);
                window.open(`${env().REACT_APP_BACKEND_URL}/api/Kurento/file?name=${record.fileName}`, '_blank');
            }} key={key} className={`chat-records-item${fullMode && !showChatsList ? ' chat-records-item_compact' : ''}${record.status !== RecordStatus.Available ? ' chat-records-item_disabled' : ''}`}><div>
                {formatDate(record.endTime, "HH:mm:ss, dd.MMM.yyyy")} ({getStattus(record.status)}), ({timeDiff(new Date(record.endTime), new Date(record.startTime))})</div>
                {record.status === RecordStatus.Available && <DownloadIcon />}
                </div>)}
        </div> : <div className={'empty-records-container'}>
                <EmptyIcon />
                <div>Видеозаписи не найдены</div>
            </div>}
    </>;

    return <div className={`app-container ${fullChatMode ? 'app-container_full-chat-mode' : ''}`}>
        {fullMode && showChatsList && <div className={`chat-list ${showList ? 'chat-list_show' : ''}`}>
            {fullChatMode && <NavigationTabs changePage={(tab) => changePage && changePage(tab)} head={contactsMode ? 'contacts' : 'processes'}/>}
            <div className="chat-search-wrapper">
                <div className="input-wrapper input-wrapper__chat-search">
                    <input value={searchChatInput} onKeyPress={event => {
                        if (event.charCode === 13) {
                            onSearchChat()
                        }
                    }} onChange={(event) => setSearchChatInput(event.target.value)} type="text"
                           className="chat-input" placeholder="Поиск чата"/>
                    <div onClick={() => onSearchChat()}><SearchIcon size={13} color={'var(--messenger-grey-3)'} /></div>
                </div>
                <div className={'sort-button'} onClick={() => setShowSort(true)}><SortIcon ref={sortRef}>
                    <div style={{display: showSort ? 'flex' : 'none'}}
                         className="message-box-settings-container">
                        <div className={`message-box-settings-item ${sortDirection === SortDirection.Unred ? 'message-box-settings-item_active' : ''}`} onClick={() => sortMessages(SortDirection.Unred)}>Непрочитанные сверху {sortDirection === SortDirection.Unred && <SelectedIcon />}</div>
                        <div className={`message-box-settings-item ${sortDirection === SortDirection.New ? 'message-box-settings-item_active' : ''}`} onClick={() => sortMessages(SortDirection.New)}>Новые сверху {sortDirection === SortDirection.New && <SelectedIcon />}</div>
                        <div className={`message-box-settings-item ${sortDirection === SortDirection.Old ? 'message-box-settings-item_active' : ''}`} onClick={() => sortMessages(SortDirection.Old)}>Старые сверху {sortDirection === SortDirection.Old && <SelectedIcon />}</div>
                    </div>
                </SortIcon></div>
                {props.contactsMode && <div onClick={showAddContact} className={'add-chat-button'}><PlusIcon /></div>}
            </div>
            <div className="chats-list">
                {searchChats?.map((chat: any, key: number) => <div onClick={() => changeChat(chat)} className={`chat-item ${chat.id === chatId ? 'chat-item_selected' : ''}`} key={key}>
                    {props.contactsMode && <div style={{position: 'relative'}}>
                        <UserPhoto sessionId={authSessionId} userId={getPhotoUserId(chat?.users ?? [])}/>
                        {chat.room.roomType === PRIVATE_CHAT && isOnline(chat.users) && <span style={{position: 'absolute', top: 35, right: 2}} className={'chat-item__name-is-online'}/>}
                    </div>}
                    <div className={"chat-item-content"}>
                    <div className={`chat-item__name`}>{chat.room.roomType !== PRIVATE_CHAT ? chat.room.name : getPrivatChatTitle(chat.users)}</div>
                    {chat.room.roomType === PRIVATE_CHAT ? <>
                        <div className={`chat-item__user-name`}>{getPrivatChatDescription(chat.users)}</div>
                        {chat.messages?.length > 0 && <>
                            <div className={`chat-item__text`}>{chat.messages?.[0]?.messageText?.length > 0 ? chat.messages?.[0]?.messageText : chat.messages?.[0]?.attachments?.length > 0 ? "Вложение" : ""}</div>
                        </>}
                        </> :
                    chat.messages?.length > 0 && <>
                        <div className={`chat-item__user-name`}>{chat.users.find((x: ChatUser) => x.userId === chat.messages?.[0]?.userId)?.userName}</div>
                        <div className={`chat-item__text`}>{chat.messages?.[0]?.messageText?.length > 0 ? chat.messages?.[0]?.messageText : chat.messages?.[0]?.attachments?.length > 0 ? "Вложение" : ""}</div>
                    </>}
                    </div>
                    <div className={"chat-item-info"}>
                        <div className={"chat-time"}>{chat.messages?.[0]?.createdDate ? getMessageTime(chat.messages?.[0]?.createdDate) : getMessageTime(chat.updatedDate)}</div>
                        {chat.unreadUserMessagesCount > 0 && <div className={`unread-messages-count`}>+{chat.unreadUserMessagesCount > 99 ? `99` : chat.unreadUserMessagesCount}</div>}
                    </div>
                </div>)}
            </div>
        </div>}
        <div className={`chat-main ${showList ? '' : 'chat-main_show'}`}>
            {fullMode && !room ? <div className={'chat-select-message'}>{chats?.length > 0 ? 'Выберите чат из списка' : ''}</div> :
            <>
            {fullMode && showChatsList && <div className="chat-back-to-list" onClick={() => setShowList(true)}>
                <BackIcon /> Назад к списку
            </div>}
            {fullMode && chatName && chatName.length > 0 && <div className={`chat-header${fullMode && !showChatsList ? ' chat-header_compact' : ''}${externalRoomId ? ' chat-header_external' : ''}`}>
                <div onClick={toProcess} className={`chat-name ${!props.contactsMode && roomContext  && props.contextUrl && props.contextUrl.length > 0 ? "chat-name_link" : ""}`}>
                    {props.contactsMode && <div style={{position: 'relative'}}>
                        <UserPhoto sessionId={authSessionId} userId={getPhotoUserId(users ?? [])}/>
                        {isOnline(users) && <span style={{position: 'absolute', top: 35, right: 2}} className={'chat-item__name-is-online'}/>}
                    </div>}
                    {props.contactsMode ? <div>{getPrivatChatTitle(users)}</div> : chatName}
                </div>
                <div className={'chat-header-tools'}>
                    {props.contactsMode && <CallIcon color={isOnline(users) ? 'var(--messenger-blue)' : '#DADADA'} ref={callRef} className={isOnline(users) ? "chat-settings" : "chat-settings-disabled"} onClick={() => {
                        isOnline(users) && setShowCallSettings(true)
                    }}>
                        <div style={{display: showCallSettings ? 'flex' : 'none'}} className="message-box-settings-container">
                            <div className="message-box-settings-item" onClick={() => onCall(true)}><div className="mesage-box-settings-item-icon"><CallBlackIcon /></div>Аудио звонок</div>
                            <div className="message-box-settings-item" onClick={() => onCall(false)}><div className="mesage-box-settings-item-icon"><CameraBlackIcon /></div>Видео звонок</div>
                        </div>
                    </CallIcon>}
                <SettingsIcon ref={ref} className="chat-settings" onClick={() => {
                    setShowChatSettings(true)
                }}>
                    <div style={{display: showChatSettings ? 'flex' : 'none'}}
                         className={"message-box-settings-container message-box-settings-container_main"}>
                        <div className="message-box-settings-item" onClick={onShowSearchMessegies}>Поиск сообщений</div>
                        {env().REACT_APP_SHOW_VIDEOCHAT_LINK && <div className="message-box-settings-item" onClick={openVideoLink}>Видеосвязь</div>}
                        <div className="message-box-settings-item" onClick={onShowRecords}>Видеозаписи</div>
                        <div className="message-box-settings-item" onClick={onShowUsers}>Информация</div>
                        {props.contactsMode && <div className="message-box-settings-item message-box-settings-item_red" onClick={onDeleteChat}>Удалить чат</div>}
                    </div>
                </SettingsIcon>
                </div>
            </div>}
            {showSearchMessegies && <div className={`communication-search-message-container ${externalRoomId ? 'communication-search-message-container_external' : ''}`}>
                <div className={`input-wrapper input-wrapper__message-search ${externalRoomId ? 'input-wrapper__message-search_external' : ''}`}>
                    <input value={searchMessageInput} onKeyPress={event => {
                        if (event.charCode === 13) {
                            searchMessageInChat()
                        }
                    }} onChange={(event) => setSearchMessageInput(event.target.value)} type="text"
                           className="chat-input" placeholder="Поиск сообщений"/>
                    <SearchIcon size={13} color={'var(--messenger-grey-3)'} />
                </div>
                <div>
                    <div onClick={() => searchPrevMessage()} className={filteredMessages.length === 0 || filteredMessages.findIndex(x => x.id === findMessageId) === 0 ? 'tm-communication-arrow-disabled' : ''}><ArrowUpIcon /></div>
                    <div onClick={() => searchNextMessage()} className={filteredMessages.length === 0 || filteredMessages.findIndex(x => x.id === findMessageId) === filteredMessages.length - 1 ? 'tm-communication-arrow-disabled' : ''}><ArrowDownIcon /></div>
                </div>
                <div onClick={closeMessageSearch} className={"chat-message-search-close"}><CloseIcon size={10}/></div>
            </div>}    
            {!showRecords && <div className="chat-wrapper" ref={messagesEndRef}>
                {users.length > 0 && messages.length > 0 && Object.keys(getGroupedMessages()).map((data: any, key: any) => <Fragment key={key}><div className="chat-date" >{getDateText(data)}</div>
                    {getGroupedMessages(data).map((message: any, key: any, groupedMessages: any) =>
                        <Messege key={key} search={searchMessageInput} highlightedMessageId={findMessageId} message={message} sameNextUser={key !== groupedMessages.length - 1 && groupedMessages[key + 1]?.userId === message?.userId} samePrevUser={key !== 0 && groupedMessages[key - 1]?.userId === message?.userId} visible={visible} messages={messages} setMessage={setMessage} deleteMessage={deleteMessage} setEditMessageId={setEditMessageId} userId={roomUserId}
                                 setReplyMessageId={setReplyMessageId} setAttachments={setAttachments} readMessage={readMessage} unreadMessages={unreadMessages} setShowSettings={setShowSettings} showSettings={showSettings} users={users} compact={fullMode && !showChatsList}/>
                    )}
                </Fragment>)}
            </div>}
            {showRecords && recordsList}
            {!showRecords && <div className={`chat-input-wrapper${fullMode && !showChatsList ? ' chat-input-wrapper_compact' : ''} ${externalRoomId ? 'chat-input-wrapper_external' : ''}`}>
                <div className="chat-input-container">
                    {editMessageId && editMessage}
                    {replyMessageId && replyMessage}
                    <div className={"chat-input-main-container"}>
                    {!sendFile ?
                    <label className="add-file-btn">
                        <input type="file" disabled={sendDisabled} multiple accept={env().REACT_APP_CHAT_FILE_TYPES?.length > 0 ? env().REACT_APP_CHAT_FILE_TYPES : "*"} onChange={onChangeFile} />
                        <AttachmentIcon />
                    </label> :
                    <div className='add-file-btn'>
                        <TmLoader isSmall={true} />
                    </div>}
                    <div className={`input-wrapper`} style={{maxHeight: 150, overflow: 'auto'}}>
                        <textarea style={{height: 26}} rows={1} ref={textareaRef} value={message} disabled={sendDisabled} onKeyPress={event => {
                            if (event.charCode === 13 && !event.shiftKey) {
                                event.preventDefault();
                                if (!isSendDisabled()) {
                                    sendMessage();
                                }
                            }
                        }} onChange={(event) => setMessage(event.target.value)}
                               className="chat-input" placeholder="Напишите сообщение…"/>
                    </div>
                    <div ref={refs.setReference} {...getReferenceProps()} onClick={sendMessage}
                         className={`chat-send-btn ${isSendDisabled() ? 'chat-send-btn_disabled' : ''}`}>
                        <SendIcon color={isSendDisabled() ? "#DADADA" : "var(--messenger-blue)"}/>
                    </div>
                    {isOpen && !canWrite && (
                        <div className="chat-tooltip" ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
                            <FloatingArrow ref={arrowRef} context={context} style={{ transform: 'translateY(-1px)' }} className="fill-white"/>
                            {env().REACT_APP_CHAT_SEND_DISABLED_MESSAGE}
                        </div>
                    )}
                    </div>
                </div>
                <div className="attachments-container">
                    {attachments?.map((attachment: any, key: number) => <div className="attachment-item" key={key}>
                        <div className="attachment-item__text">{attachment.fileName}<FileIcon /></div>
                        <div className="attachment-item__delete-btn" onClick={() => removeAttachment(attachment)}><DeleteIcon /></div>
                    </div>)}
                </div>
            </div>}
            </>}
        </div>
    </div>
}